import type { RawAxiosRequestConfig } from 'axios'
import axios from 'axios'
import type { Operation } from 'fast-json-patch'

import type {
  BasicDigitalTwin,
  BasicRelationship,
  DigitalTwinsFile,
  DtdlInterface,
  JobCheckStatusResponse,
  JobStatus
} from 'src/models'
import { useGlobalStore } from 'src/stores/global'
import { getApiToken } from './auth'

/** Custom batch operation format */
export interface BatchFile {
  put?: {
    digitaltwins?: BasicDigitalTwin[]
    relationships?: BasicRelationship[]
  }
  patch?: {
    digitaltwins?: {
      twinId: string
      patch: Operation[]
    }[]
    relationships?: {
      twinId: string
      relationshipId: string
      patch: Operation[]
    }[]
  }
  delete?: {
    /** Twins to be deleted */
    digitaltwins?: string[]
    /** Relationships to be deleted (requires at least source id and relationship id in each object) */
    relationships?: BasicRelationship[]
  }
  /** DTDL models to be loaded */
  models?: DtdlInterface[]
  /* A list of model ids that should be loaded from the model repository */
  modelIds?: string[]
  /* A list of ontologies that should be loaded from the model repository */
  ontologies?: string[]
}

export interface BatchStatus {
  done: number
  failed: number
  total: number
}

interface ActivityResult {
  done: number
  failed: number
  exceptions?: {
    // Can be either the twin id or the model id
    id: string
    relationshipId?: string
    message: string
  }[]
}
export interface BatchOrchestrationSubResult {
  digitaltwins?: ActivityResult
  relationships?: ActivityResult
}

/** Result of a batch job */
export interface BatchOrchestrationResult extends BatchStatus {
  put?: BatchOrchestrationSubResult
  patch?: BatchOrchestrationSubResult
  delete?: BatchOrchestrationSubResult
  models?: ActivityResult
}

interface BatchJobStatus extends JobStatus {
  customStatus: BatchStatus
  output: BatchOrchestrationResult
}

const isJobCheckStatusResponse = (
  resp: JobCheckStatusResponse | BatchOrchestrationResult
): resp is JobCheckStatusResponse =>
  typeof (resp as JobCheckStatusResponse).id === 'string' &&
  typeof (resp as JobCheckStatusResponse).statusQueryGetUri === 'string'

class TwinsBatchApiService {
  private _config: RawAxiosRequestConfig<unknown> | undefined

  /** POST a batch job */
  public async batch(job: DigitalTwinsFile | BatchFile | File) {
    await this.initialize()

    const response = await axios.post<JobCheckStatusResponse | BatchOrchestrationResult>(
      `${import.meta.env.batchApi}`,
      job,
      this._config
    ) // endpoint is api/digitaltwins-batch

    return response.data
  }

  /** GET a batch job status */
  public async getBatchJobStatus(statusQueryGetUri: string) {
    await this.initialize()

    const response = await axios.get<BatchJobStatus>(statusQueryGetUri, this._config)

    return response.data
  }

  /** periodically retrieve status of the running job */
  public async getBatchJobStatusRepeated(
    statusQueryGetUri: string,
    update?: (customStatus: BatchStatus) => void,
    done?: (output: BatchOrchestrationResult) => void
  ): Promise<BatchOrchestrationResult> {
    // periodically retrieve status of the running job
    return new Promise<BatchOrchestrationResult>((resolve, reject) => {
      const repeatGetJobStatus = async () => {
        try {
          const response = await this.getBatchJobStatus(statusQueryGetUri)
          if (typeof update === 'function') update(response.customStatus)
          if (response?.runtimeStatus === 'Running' || response?.runtimeStatus === 'Pending') {
            // run again if the job is still running
            setTimeout(repeatGetJobStatus, 2000)
          } else if (
            response?.runtimeStatus === 'Failed' ||
            response?.runtimeStatus === 'Canceled' ||
            response?.runtimeStatus === 'Terminated' ||
            response?.runtimeStatus === 'TimedOut'
          ) {
            throw new Error(`Batch job failed with status: ${response.runtimeStatus}`)
          } else {
            if (typeof done === 'function') done(response.output)
            resolve(response.output)
          }
        } catch (error) {
          reject(error)
        }
      }
      repeatGetJobStatus()
    })
  }

  /**
   * Run a batch job on the backend
   * @param job
   * @param update Callback on every update
   * @param done Callback on job completion
   * @returns A promise that resolves when the the job is done
   */
  public async runTwinsBatchJob(
    job: DigitalTwinsFile | BatchFile,
    update?: (customStatus?: BatchStatus) => void,
    done?: (output?: BatchOrchestrationResult) => void
  ): Promise<BatchOrchestrationResult> {
    const jobResponse = await twinsBatchApiService.batch(job)
    if (isJobCheckStatusResponse(jobResponse)) {
      const statusQueryGetUri: string = jobResponse?.statusQueryGetUri
      return twinsBatchApiService.getBatchJobStatusRepeated(
        statusQueryGetUri,
        (customStatus) => {
          if (typeof update === 'function') update(customStatus)
        },
        (output) => {
          if (typeof done === 'function') done(output)
        }
      )
    } else {
      // The batch job may return the output immediately for small batches (without durable functions)
      if (typeof done === 'function') done(jobResponse)
      return jobResponse
    }
  }

  private async initialize() {
    const authResponse = await getApiToken()
    if (!authResponse?.accessToken || !authResponse.expiresOn) return

    const { environmentId } = useGlobalStore()
    if (!environmentId) throw new Error('Environment id not found')
    this._config = {
      headers: {
        Authorization: 'Bearer ' + authResponse.accessToken,
        'x-adt-id': environmentId // environmentId.value
      }
    }
  }
}

export const twinsBatchApiService = new TwinsBatchApiService() // new CachedTwinsApiService()
